"use client";
import React, { createContext, useContext, useState, ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

export type TEntryType =
  | "article"
  | "publication"
  | "edition"
  | "parution"
  | "podcast";

interface PublicationsFilterContextProps {
  entryTypes: TEntryType[] | null;
  setEntryTypes: (entryTypes: TEntryType[]) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  page: number;
  setPage: (value: number) => void;
  visibleMobile: boolean;
  setVisibleMobile: (value: boolean) => void;
}

const PublicationsFilterContext = createContext<
  PublicationsFilterContextProps | undefined
>(undefined);

export const PublicationsSearchProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const searchParams = useSearchParams();
  const typesFromQs = searchParams.get("types");

  const [entryTypes, setEntryTypes] = useState<TEntryType[] | null>(
    typesFromQs?.split(",") as TEntryType[]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleMobile, setVisibleMobile] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const pathname = usePathname();
  const router = useRouter();

  React.useEffect(() => {
    if (entryTypes && entryTypes.length > 0) {
      router.replace(`${pathname}?types=${entryTypes?.join(",")}`, {
        scroll: false,
      });
    }
  }, [page, entryTypes]);

  React.useEffect(() => {
    setPage(1);
  }, [entryTypes]);

  return (
    <PublicationsFilterContext.Provider
      value={{
        entryTypes,
        setEntryTypes,
        loading,
        setLoading,
        page,
        setPage,
        visibleMobile,
        setVisibleMobile,
      }}
    >
      {children}
    </PublicationsFilterContext.Provider>
  );
};

export const usePublicationsFilter = (): PublicationsFilterContextProps => {
  const context = useContext(PublicationsFilterContext);
  if (!context) {
    throw new Error(
      "usePublicationsSearch must be used within a PublicationsSearchProvider"
    );
  }
  return context;
};
